import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    pets: Array,
    dates: Array,
    orderId: String,
    orderItemId: String
  }

  connect() {
    // Bind the handler once so we can remove it later
    this.boundHandleActivityConfirmed = this.handleActivityConfirmed.bind(this)

    // Listen for activity modal confirmation events (use document to survive Turbo updates)
    document.addEventListener('activity-modal:confirmed', this.boundHandleActivityConfirmed)
  }

  disconnect() {
    document.removeEventListener('activity-modal:confirmed', this.boundHandleActivityConfirmed)
  }

  // Get the activity modal fresh each time (survives Turbo updates)
  get activityModal() {
    return document.getElementById('activity-modal')
  }

  openActivityModal(event) {
    event.preventDefault()
    event.stopPropagation()

    // Get activity data from the clicked element
    const activityData = {
      id: event.currentTarget.dataset.activityId,
      location_activity_id: event.currentTarget.dataset.locationActivityId,
      title: event.currentTarget.dataset.activityTitle,
      description: event.currentTarget.dataset.activityDescription,
      description_html: event.currentTarget.dataset.activityDescriptionHtml,
      icon: event.currentTarget.dataset.activityIcon,
      price: event.currentTarget.dataset.activityPrice,
      // pet_type restriction: 'dog', 'cat', 'other', or empty/null (available for all)
      pet_type: event.currentTarget.dataset.activityPetType || null,
      // Available time slots from PTP (e.g., ["am", "noon", "pm"])
      available_times: JSON.parse(event.currentTarget.dataset.activityAvailableTimes || '[]'),
      // Day availability restrictions
      available_saturdays: event.currentTarget.dataset.activityAvailableSaturdays === 'true',
      available_sundays: event.currentTarget.dataset.activityAvailableSundays === 'true',
      available_public_holidays: event.currentTarget.dataset.activityAvailablePublicHolidays === 'true',
      // Medication-grouped activities require the customer to enter
      // drug/dose/instructions in the notes field.
      is_medication: event.currentTarget.dataset.activityIsMedication === 'true',
      // Activity-level notes (special instructions)
      activity_notes: event.currentTarget.dataset.activityNotes || '',
      // Per-date notes (special requests for each day)
      date_notes: JSON.parse(event.currentTarget.dataset.activityDateNotes || '{}')
    }

    // Get pets and dates - prefer button data attributes (always fresh after Turbo updates)
    // Fall back to Stimulus values if button attrs are missing
    let pets = JSON.parse(event.currentTarget.dataset.pets || '[]')
    let dates = JSON.parse(event.currentTarget.dataset.dates || '[]')

    // Fallback to Stimulus values if button attributes are empty
    if (pets.length === 0) {
      pets = this.petsValue || []
    }
    if (dates.length === 0) {
      dates = this.datesValue || []
    }

    // Get existing selections if updating
    const existingSelections = event.currentTarget.dataset.activitySelections
      ? JSON.parse(event.currentTarget.dataset.activitySelections)
      : []

    // Get the activity modal controller and open it
    if (this.activityModal) {
      const modalController = this.application.getControllerForElementAndIdentifier(
        this.activityModal,
        'activity-modal'
      )

      if (modalController) {
        modalController.open(activityData, pets, dates, existingSelections)
      }
    }
  }

  handleActivityConfirmed(event) {
    const { activity, selections, activityNotes } = event.detail

    // Submit the selections to the server
    this.saveActivitySelections(activity, selections, activityNotes)
  }

  async saveActivitySelections(activity, selections, activityNotes) {
    const orderId = this.orderIdValue
    const orderItemId = this.orderItemIdValue
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const requestBody = {
      order_item_id: orderItemId,
      activity_id: activity.id,
      location_activity_id: activity.location_activity_id,
      selections: selections,
      activity_notes: activityNotes || ''
    }

    try {
      const response = await fetch(`/flow/booking/guest_services/add_activity?order_id=${orderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify(requestBody)
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        console.error('Failed to save activity selections')
      }
    } catch (error) {
      console.error('Error saving activity selections:', error)
    }
  }

  handleSelectionConfirmed(event) {
    // Legacy handler for pet taxi modal
    console.log('Selection confirmed:', event.detail)
  }
}
